import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Loader } from '@lms/ui';
import { AuthenticatedImage } from '@/components/AuthenticatedImage';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { dashboardCourseHref } from '@/lib/course-admin-navigation';
import { STATUS_LABELS, type SupportTicket, type SupportTicketStatus } from '@/features/support/types';

const DASHBOARD_SIDEBAR_LIMIT = 3;
const TIME_ZONE = 'America/Santiago';

interface EnrolledCourse {
  id: string;
  title: string;
  name?: string;
  code?: string;
  instructorName?: string;
  lastLessonTitle?: string;
  playSectionItemId?: string | null;
  progress?: number;
  progressPercent?: number;
  coverImageUrl?: string | null;
  bannerImageUrl?: string | null;
}

type CalendarEventKind =
  | 'course_start'
  | 'course_end'
  | 'available'
  | 'deadline'
  | 'assessment_open'
  | 'assessment_close';

interface CalendarEvent {
  id: string;
  kind: CalendarEventKind;
  title: string;
  courseId: string;
  courseName: string;
  sectionItemId?: string;
  at: string;
  isCompleted: boolean;
}

type DeliveryTone = 'urgent' | 'soon' | 'normal';

function courseProgress(course: EnrolledCourse): number {
  return Math.round(course.progressPercent ?? course.progress ?? 0);
}

function courseTitle(course: EnrolledCourse): string {
  return course.title ?? course.name ?? 'Curso';
}

function userInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function courseShortLabel(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 2) return name;
  return words.slice(0, 2).join(' ');
}

function isActiveCalendarEvent(event: CalendarEvent, now: Date): boolean {
  if (event.isCompleted) return false;
  const at = new Date(event.at);
  if (at <= now) {
    return event.kind === 'deadline' || event.kind === 'assessment_close';
  }
  return true;
}

function isDeliveryEvent(kind: CalendarEventKind): boolean {
  return kind === 'deadline' || kind === 'assessment_close' || kind === 'assessment_open';
}

function formatDeliveryWhen(iso: string, now: Date): { label: string; tone: DeliveryTone } {
  const date = new Date(iso);
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.round((date.getTime() - now.getTime()) / dayMs);
  const time = new Intl.DateTimeFormat('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  }).format(date);

  if (diffDays <= 0) {
    return { label: `Hoy, ${time}`, tone: 'urgent' };
  }
  if (diffDays === 1) {
    return { label: `Mañana, ${time}`, tone: 'soon' };
  }

  const monthDay = new Intl.DateTimeFormat('es-CL', {
    day: 'numeric',
    month: 'long',
    timeZone: TIME_ZONE,
  }).format(date);

  return { label: monthDay.charAt(0).toUpperCase() + monthDay.slice(1), tone: 'normal' };
}

function deliveryMeta(event: CalendarEvent): { left: string; right: string; href: string } {
  const href = dashboardCourseHref(event.courseId, event.sectionItemId);

  if (event.kind === 'assessment_open') {
    return { left: 'Evaluación', right: 'Pendiente apertura', href };
  }
  if (event.kind === 'assessment_close') {
    return { left: 'Evaluación', right: 'Ver actividad →', href };
  }
  return { left: 'Actividad', right: 'Ver actividad →', href };
}

function coursePlayHref(course: EnrolledCourse): string {
  return dashboardCourseHref(course.id);
}

function ticketStatusClass(status: SupportTicketStatus): string {
  if (status === 'en_progreso') return 'dash-support-ticket-status dash-support-ticket-status--review';
  if (status === 'abierto') return 'dash-support-ticket-status dash-support-ticket-status--open';
  if (status === 'resuelto') return 'dash-support-ticket-status dash-support-ticket-status--resolved';
  return 'dash-support-ticket-status dash-support-ticket-status--closed';
}

interface CourseDashboardCardProps {
  course: EnrolledCourse;
}

function CourseDashboardCard({ course }: CourseDashboardCardProps) {
  const percent = courseProgress(course);
  const image = course.coverImageUrl ?? course.bannerImageUrl;
  const title = courseTitle(course);

  return (
    <article className="dash-course-card">
      <div className="dash-course-card__media">
        {image ? (
          <AuthenticatedImage src={image} alt="" />
        ) : (
          <div className="dash-course-card__placeholder" aria-hidden="true" />
        )}
      </div>
      <div className="dash-course-card__body">
        <div className="dash-course-card__meta">
          <span className="dash-course-card__code">{course.code ?? 'CURSO'}</span>
          <span className="dash-course-card__instructor">
            <span className="material-symbols-outlined text-[16px]" aria-hidden="true">
              person
            </span>
            {course.instructorName ?? 'Sin asignar'}
          </span>
        </div>
        <h3 className="dash-course-card__title">{title}</h3>
        <p className="dash-course-card__lesson">
          <span className="material-symbols-outlined text-[16px] text-[var(--dash-secondary)]" aria-hidden="true">
            play_circle
          </span>
          <span>
            Última lección:{' '}
            <strong className="dash-course-card__lesson-name">{course.lastLessonTitle ?? 'Comienza el curso'}</strong>
          </span>
        </p>
        <div className="dash-course-card__progress">
          <div className="dash-course-card__progress-labels">
            <span>Progreso del plan</span>
            <span>{percent}%</span>
          </div>
          <div className="dash-course-card__progress-track">
            <div className="dash-course-card__progress-fill" style={{ width: `${percent}%` }} />
          </div>
        </div>
      </div>
      <div className="dash-course-card__action">
        <Link to={coursePlayHref(course)} className="dash-course-card__btn">
          <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
            play_arrow
          </span>
          Continuar
        </Link>
      </div>
    </article>
  );
}

export function DashboardPage() {
  const { user, isAuthenticated } = useAuth();
  const { hasPermission } = usePermissions();

  const canViewCourses = hasPermission(PERMISSIONS.MIS_CURSOS_VER);
  const canViewSupport = hasPermission(PERMISSIONS.MIS_SOPORTE_VER);
  const canManageSupport = hasPermission(PERMISSIONS.SOPORTE_VER);
  const displayName = user?.name?.split(' ')[0] ?? 'usuario';

  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ['student', 'courses'],
    queryFn: () => api<{ data: EnrolledCourse[] }>('/student/courses'),
    enabled: canViewCourses && isAuthenticated,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });

  const { data: calendarData, isLoading: calendarLoading } = useQuery({
    queryKey: ['student', 'calendar'],
    queryFn: () => api<{ data: CalendarEvent[] }>('/student/calendar'),
    enabled: canViewCourses && isAuthenticated,
  });

  const { data: ticketsData } = useQuery({
    queryKey: ['student', 'support', 'tickets'],
    queryFn: () => api<{ data: SupportTicket[] }>('/student/support/tickets'),
    enabled: canViewSupport && !canManageSupport && isAuthenticated,
  });

  const { data: newTicketsData, isLoading: newTicketsLoading } = useQuery({
    queryKey: ['support', 'admin', 'tickets', 'abierto', 'count'],
    queryFn: () =>
      api<{ meta: { total: number } }>('/support/tickets?status=abierto&page=1&perPage=1'),
    enabled: canManageSupport && isAuthenticated,
  });

  const { data: progressTicketsData, isLoading: progressTicketsLoading } = useQuery({
    queryKey: ['support', 'admin', 'tickets', 'en_progreso', 'count'],
    queryFn: () =>
      api<{ meta: { total: number } }>('/support/tickets?status=en_progreso&page=1&perPage=1'),
    enabled: canManageSupport && isAuthenticated,
  });

  const newTicketCount = newTicketsData?.meta.total ?? 0;
  const progressTicketCount = progressTicketsData?.meta.total ?? 0;

  const now = useMemo(() => new Date(), [coursesData, calendarData, ticketsData]);

  const courses = useMemo(() => coursesData?.data ?? [], [coursesData?.data]);

  const activeCourses = useMemo(() => {
    const inProgress = courses
      .filter((course) => courseProgress(course) < 100)
      .sort((a, b) => courseProgress(b) - courseProgress(a));
    if (inProgress.length > 0) return inProgress;
    return courses;
  }, [courses]);

  const calendarEvents = useMemo(() => {
    return (calendarData?.data ?? []).filter((event) => isActiveCalendarEvent(event, now));
  }, [calendarData?.data, now]);

  const priorityTodayCount = useMemo(() => {
    return calendarEvents.filter((event) => {
      if (!isDeliveryEvent(event.kind)) return false;
      const { tone } = formatDeliveryWhen(event.at, now);
      return tone === 'urgent';
    }).length;
  }, [calendarEvents, now]);

  const weekProgress = useMemo(() => {
    if (courses.length === 0) return 0;
    const total = courses.reduce((sum, course) => sum + courseProgress(course), 0);
    return Math.round(total / courses.length);
  }, [courses]);

  const welcomeSubtitle = useMemo(() => {
    if (!canViewCourses) {
      return 'Desde aquí puedes acceder a las herramientas de administración del sitio.';
    }

    const parts = ['Continúa con tu aprendizaje donde lo dejaste.'];
    if (priorityTodayCount > 0) {
      parts.push(
        `Tienes ${priorityTodayCount} ${priorityTodayCount === 1 ? 'actividad prioritaria' : 'actividades prioritarias'} prevista${priorityTodayCount === 1 ? '' : 's'} para hoy`,
      );
    }
    if (weekProgress > 0) {
      parts.push(`tus metas de la semana van al ${weekProgress}%`);
    }

    return `${parts.join(' y ')}.`;
  }, [canViewCourses, priorityTodayCount, weekProgress]);

  const upcomingDeliveries = useMemo(() => {
    return calendarEvents
      .filter((event) => isDeliveryEvent(event.kind))
      .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
      .slice(0, DASHBOARD_SIDEBAR_LIMIT);
  }, [calendarEvents]);

  const urgentDeliveryCount = useMemo(() => {
    return upcomingDeliveries.filter((event) => formatDeliveryWhen(event.at, now).tone === 'urgent').length;
  }, [upcomingDeliveries, now]);

  const recentTickets = useMemo(() => {
    return [...(ticketsData?.data ?? [])]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, DASHBOARD_SIDEBAR_LIMIT);
  }, [ticketsData?.data]);

  const isLoading = canViewCourses && (coursesLoading || calendarLoading);

  return (
    <div className="lms-dashboard lms-site-admin-hub-page font-sans">
      <div className="dash-shell">
        <section className="dash-welcome" aria-label="Bienvenida">
          <div className="dash-welcome__profile">
            <div className="dash-welcome__avatar-wrap">
              <div className="dash-welcome__avatar" aria-hidden="true">
                {userInitials(user?.name ?? displayName)}
              </div>
              <span className="dash-welcome__status" aria-hidden="true" />
            </div>
            <div className="dash-welcome__copy">
              <h1 className="dash-welcome__title">¡Bienvenido de nuevo, {displayName}!</h1>
              <p className="dash-welcome__subtitle">{welcomeSubtitle}</p>
            </div>
          </div>
        </section>

        <div className="dash-layout">
          {canViewCourses && (
            <div className="dash-layout__main">
              <div className="dash-section-head">
                <div>
                  <h2 className="dash-section-head__title">Mis Cursos</h2>
                  <p className="dash-section-head__subtitle">
                    Reanuda tus clases multimedia y material didáctico asignado
                  </p>
                </div>
                <Link to="/mis-cursos" className="dash-section-head__link">
                  Ver catálogo completo
                  <span className="material-symbols-outlined text-[16px]" aria-hidden="true">
                    chevron_right
                  </span>
                </Link>
              </div>

              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Loader />
                </div>
              ) : activeCourses.length > 0 ? (
                <div className="dash-course-list">
                  {activeCourses.map((course) => (
                    <CourseDashboardCard key={course.id} course={course} />
                  ))}
                </div>
              ) : (
                <div className="dash-empty-panel">
                  <p className="dash-empty-panel__text">Aún no tienes cursos inscritos.</p>
                  <Link to="/mis-cursos" className="dash-section-head__link">
                    Ver mis cursos
                    <span className="material-symbols-outlined text-[16px]" aria-hidden="true">
                      chevron_right
                    </span>
                  </Link>
                </div>
              )}
            </div>
          )}

          {canViewCourses && (
            <aside className="dash-layout__aside">
              <section className="dash-deliveries" aria-label="Próximas entregas">
                <div className="dash-deliveries__header">
                  <div className="dash-deliveries__title-wrap">
                    <span className="material-symbols-outlined text-[20px] text-[var(--dash-error)]" aria-hidden="true">
                      event_upcoming
                    </span>
                    <h3 className="dash-deliveries__title">Próximas Entregas</h3>
                  </div>
                  {urgentDeliveryCount > 0 && (
                    <span className="dash-deliveries__badge">
                      {urgentDeliveryCount} {urgentDeliveryCount === 1 ? 'Urgente' : 'Urgentes'}
                    </span>
                  )}
                </div>

                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader />
                  </div>
                ) : upcomingDeliveries.length > 0 ? (
                  <div className="dash-deliveries__list">
                    {upcomingDeliveries.map((event) => {
                      const when = formatDeliveryWhen(event.at, now);
                      const meta = deliveryMeta(event);
                      return (
                        <article key={event.id} className="dash-delivery-item">
                          <div className="dash-delivery-item__head">
                            <span className="dash-delivery-item__course">{courseShortLabel(event.courseName)}</span>
                            <span className={`dash-delivery-item__when dash-delivery-item__when--${when.tone}`}>
                              <span className="material-symbols-outlined text-[14px]" aria-hidden="true">
                                {when.tone === 'urgent' ? 'timer' : when.tone === 'soon' ? 'schedule' : 'calendar_today'}
                              </span>
                              {when.label}
                            </span>
                          </div>
                          <h4 className="dash-delivery-item__title">{event.title}</h4>
                          <div className="dash-delivery-item__footer">
                            <span>{meta.left}</span>
                            <Link to={meta.href} className="dash-delivery-item__link">
                              {meta.right}
                            </Link>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <p className="dash-empty-panel__text">No hay entregas próximas.</p>
                )}
              </section>

              {(canManageSupport || canViewSupport) && (
                <section className="dash-support" id="soporte" aria-label="Mesa de ayuda">
                  {canManageSupport ? (
                    <>
                      <div className="dash-support__header">
                        <div className="dash-support__copy">
                          <span className="dash-support__eyebrow">Mesa de Ayuda</span>
                          <h3 className="dash-support__title">Tickets por responder</h3>
                        </div>
                        <div className="dash-support__icon" aria-hidden="true">
                          <span className="material-symbols-outlined text-[22px]">support_agent</span>
                        </div>
                      </div>
                      <div className="dash-support-stats">
                        <div className="dash-support-stat">
                          <span className="dash-support-stat__label">Tickets nuevos</span>
                          <span className="dash-support-stat__value dash-support-stat__value--open">
                            {newTicketsLoading ? '…' : newTicketCount}
                          </span>
                        </div>
                        <div className="dash-support-stat">
                          <span className="dash-support-stat__label">En progreso</span>
                          <span className="dash-support-stat__value dash-support-stat__value--progress">
                            {progressTicketsLoading ? '…' : progressTicketCount}
                          </span>
                        </div>
                      </div>
                      <div className="dash-support__actions dash-support__actions--single">
                        <Link to="/soporte/admin" className="dash-support__btn dash-support__btn--primary">
                          <span className="material-symbols-outlined text-[16px]" aria-hidden="true">
                            inbox
                          </span>
                          Ver bandeja
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="dash-support__header">
                        <div className="dash-support__copy">
                          <span className="dash-support__eyebrow">Mesa de Ayuda</span>
                          <h3 className="dash-support__title">¿Necesitas soporte técnico?</h3>
                          <p className="dash-support__text">
                            Resuelve dudas sobre matrícula, accesos a laboratorios o problemas con tu aula virtual.
                          </p>
                        </div>
                        <div className="dash-support__icon" aria-hidden="true">
                          <span className="material-symbols-outlined text-[22px]">contact_support</span>
                        </div>
                      </div>

                      {recentTickets.length > 0 && (
                        <div className="dash-support-tickets">
                          {recentTickets.map((ticket) => (
                            <Link
                              key={ticket.id}
                              to={`/soporte/${ticket.id}`}
                              className="dash-support-ticket"
                            >
                              <div className="dash-support-ticket__copy">
                                <span className="dash-support-ticket__label">Ticket en Curso</span>
                                <span className="dash-support-ticket__subject">{ticket.subject}</span>
                              </div>
                              <span className={ticketStatusClass(ticket.status)}>
                                {STATUS_LABELS[ticket.status]}
                              </span>
                            </Link>
                          ))}
                        </div>
                      )}

                      <div className="dash-support__actions">
                        <Link to="/soporte?nuevo=1" className="dash-support__btn dash-support__btn--primary">
                          <span className="material-symbols-outlined text-[16px]" aria-hidden="true">
                            add
                          </span>
                          Crear Ticket
                        </Link>
                        <Link to="/soporte" className="dash-support__btn dash-support__btn--secondary">
                          <span className="material-symbols-outlined text-[16px]" aria-hidden="true">
                            inbox
                          </span>
                          Mis Tickets
                        </Link>
                      </div>
                    </>
                  )}
                </section>
              )}
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}
